import { ForwardedRef, forwardRef, MouseEvent, ReactNode, ReactPortal, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

import cn from 'classnames';

import { fontClassNames } from 'font';

import { useEventListener } from 'hooks/useEventListener';

import { Icon } from 'ui/atoms/Icon/Icon';
import { Mask } from 'ui/atoms/Mask/Mask';
import { TagLabel } from 'ui/atoms/TagLabel/TagLabel';

import styles from './Modal.module.scss';

interface Props {
  title?: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  closable?: boolean;
  maxWidth?: number;
  padding?: number;
  className?: string;
}

export const Modal = forwardRef(
  (
    { title, description, onClose, closable = true, maxWidth = 460, padding = 50, className, children }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    useEffect(() => {
      /** Disable scrolling on mount */
      document.body.style.overflow = 'hidden';

      /** Re-enable scrolling when the component unmounts */
      return () => {
        document.body.style.overflow = 'visible';
      };
    }, []);

    useEventListener('keydown', (ev: KeyboardEvent) => {
      if (ev.keyCode === 27) {
        onCloseHandler();
      }
    });

    const onCloseHandler = useCallback(() => {
      document.body.style.overflow = 'visible';

      onClose();
    }, [onClose]);

    const stopPropagation = useCallback((e: MouseEvent) => e.stopPropagation(), []);

    const modalOverlay = (
      <>
        <Mask onClick={onCloseHandler} />

        <div
          ref={ref}
          className={cn(styles['modal'], fontClassNames, className)}
          style={{ maxWidth: maxWidth, padding }}
          onClick={stopPropagation}
        >
          {title && (
            <div className={styles['modal-header']}>
              {title && <h2 className={styles['modal-title']}>{title}</h2>}

              {closable && (
                <button className={styles['close-button']} onClick={onCloseHandler}>
                  <Icon type="close-2" />
                </button>
              )}
            </div>
          )}

          {!title && closable && (
            <button className={styles['close-button-outside']} onClick={onCloseHandler}>
              <Icon type="close-2" />
            </button>
          )}

          {description && (
            <div className={styles['modal-description']}>
              <TagLabel status="active" text={description} className={styles['modal-tag']} />
            </div>
          )}

          <div className={styles['modal-content']}>{children}</div>
        </div>
      </>
    );

    return createPortal(modalOverlay, document.body) as ReactPortal;
  },
);
